import { MenuItem, Select } from "@mui/material";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

const GridSelectEditor = forwardRef((props, ref) => {
  const { value: startValue, stopEditing, api, values = [], onValueChange } = props;
  const initialVal = startValue != null ? String(startValue).trim() : "";
  const [value, setValue] = useState(initialVal);
  const valueRef = useRef(initialVal);
  const selectRef = useRef(null);

  // Ensure current value is included in options so MUI Select never shows empty or blank
  const optionList = Array.isArray(values) ? [...values] : [];
  if (initialVal && !optionList.some((v) => (v?.value ?? v) === initialVal)) {
    optionList.unshift(initialVal);
  }

  useImperativeHandle(ref, () => ({
    getValue() {
      console.log("[GridSelectEditor] getValue() called → returning:", valueRef.current);
      return valueRef.current;
    },
    afterGuiAttached: () => {
      setTimeout(() => {
        const input = selectRef.current?.querySelector('input') || selectRef.current;
        input?.focus();
      }, 50);
    },
    // isPopup intentionally omitted: when isPopup=true, AG Grid adds a document-level
    // mousedown listener that fires BEFORE MUI's onChange (MUI Menu is a Portal at
    // document.body, outside the popup container). AG Grid closes the editor and calls
    // getValue() before the selection is committed. Inline mode avoids this race entirely.
  }));

  const onChange = (e) => {
    const newValue = e.target.value;
    console.log("[GridSelectEditor] User selected:", newValue);
    valueRef.current = newValue;
    setValue(newValue);
    // AG Grid React v32 uses CellEditorComponentProxy which owns the value via onValueChange.
    // Its getValue() returns this.value (the proxy-internal copy), NOT our useImperativeHandle
    // getValue(). Calling onValueChange updates the proxy so stopEditing() commits the right value.
    onValueChange?.(newValue);

    const stop = stopEditing || api?.stopEditing;
    if (stop) stop();
  };

  return (
    <Select
      ref={selectRef}
      value={value}
      onChange={onChange}
      defaultOpen={true}
      size="small"
      style={{ width: "100%", height: "100%" }}
      MenuProps={{
        autoFocus: true,
        disableAutoFocusItem: false,
        PaperProps: {
          sx: {
            minWidth: 200,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            border: "1px solid rgba(23, 163, 74, 0.2)",
          }
        }
      }}
      sx={{
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        fontFamily: "Poppins",
        fontSize: 14,
      }}
    >
      {optionList.map((v, i) => {
        const val = v?.value ?? v;
        const label = v?.label ?? v;
        return (
          <MenuItem
            key={i}
            value={val}
            sx={{
              fontSize: 14,
              fontFamily: "Poppins",
              "&.Mui-selected": { bgcolor: "rgba(23, 163, 74, 0.1)" }
            }}
          >
            {label}
          </MenuItem>
        );
      })}
    </Select>
  );
});

export default GridSelectEditor;
